import { useState, useEffect } from "react";
import { createFileRoute } from '@tanstack/react-router'
import { useDispatch } from 'react-redux';
import moment from 'moment';

import { ProtectedRoute } from '@/components/protected-route'
import { HeaderWithNavigation } from '@/components/header-with-navigation'
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

import { 
  View, 
  SS_Button, 
  SS_Text, 
  SS_FadeInView, 
  SS_TabPill, 
  SS_Label,
  SS_Divider,
  Image as SS_Image
} from '@/components/native-wrapper-ui';

import JobCustomerView from '@/components/jobs/job-customer';
import JobDetailsView from '@/components/jobs/job-details';

import { useAuth } from '@/contexts/auth-context';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';

export const Route = createFileRoute('/lookup')({
  component: () => (
    <ProtectedRoute>
      <LookupPage />
    </ProtectedRoute>
  ),
})

function LookupPage() {
  const { user } = useAuth();
  const dispatch = useDispatch();

  const [oldJob, setOldJob] = useState<any>(null);
  const [specArr, setSpecArr] = useState<any[]>([]);
  const [lookupID, setLookupID] = useState('');
  const [activeTab, setActiveTab] = useState('info');
  const [processing, setProcessing] = useState(false);

  const lookupJob = async () => {
    if (!lookupID || !user) return;

    setProcessing(true);
    try {
      // Set auth token for this request
      const response = await apiClient.get(`/find-job?id=${lookupID}`);
      const data = response.data;
      
      if (data.id) {
        setOldJob(data);
      } else {
        toast.error(data.msg || 'No job found');
      }
    } catch (error: any) {
      console.error('Lookup error:', error);
      toast.error(error.response?.data?.msg || 'Error looking up job');
    } finally {
      setProcessing(false);
    }
  };

  const _setTab = (index: number, tab: string) => {
    setActiveTab(tab);
  };

  const clearSearch = () => {
    setLookupID('');
    setOldJob(null);
    setActiveTab('info');
  };

  // Process specifications when job loads
  useEffect(() => {
    if (!oldJob) {
      setSpecArr([]);
      return;
    }

    const specs: any[] = [];
    
    if (
      oldJob.plot?.development?.region?.company?.description &&
      oldJob.plot.development.region.company.description.trim() !== ''
    ) {
      specs.push([
        'Company Specifications',
        oldJob.plot.development.region.company.description,
      ]);
    }
    
    if (
      oldJob.plot?.development?.region?.description &&
      oldJob.plot.development.region.description.trim() !== ''
    ) {
      specs.push([
        'Regional Specifications',
        oldJob.plot.development.region.description,
      ]);
    }
    
    if (
      oldJob.plot?.development?.description &&
      oldJob.plot.development.description.trim() !== ''
    ) {
      specs.push([
        'Development Specifications',
        oldJob.plot.development.description,
      ]);
    }
    
    if (oldJob.specifications && oldJob.specifications.trim() !== '') {
      specs.push(['Job Specifications', oldJob.specifications]);
    }

    setSpecArr(specs);
  }, [oldJob]);

  if (!oldJob) {
    return (
      <>
        <HeaderWithNavigation />

        <div className="flex-1 flex flex-col bg-white">
          <div className="flex-1">
            <div className="flex-1 pt-6 bg-gray-50">
              <div className="mx-8 flex flex-row">
                <div className="border flex-1 border-r-0 border-gray-300 flex flex-row">
                  <Input
                    className="p-4 text-black flex-1 bg-white text-lg rounded-none border-0"
                    placeholder="Lookup by Job ID…"
                    value={lookupID}
                    onChange={(e) => setLookupID(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && lookupJob()}
                    type="number"
                    autoFocus
                  />

                  {lookupID.length > 0 && (
                    <div className="bg-white">
                      <Button
                        variant="ghost"
                        className="p-4"
                        onClick={clearSearch}
                      >
                        ×
                      </Button>
                    </div>
                  )}
                </div>

                <Button
                  disabled={!lookupID.length}
                  className="rounded-none rounded-r w-32"
                  onClick={lookupJob}
                >
                  {processing ? 'Loading...' : '→'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <HeaderWithNavigation />
      
      <div className="flex-1 flex flex-col bg-white">
        <div className="flex-1">
          <div className="flex-1 flex flex-row">
            <div className="flex-1">
              <div className="flex-1">
                <div className="flex flex-row border-b border-gray-300">
                  <SS_Text className="m-0 pl-8 pr-2 py-3 text-black font-bold">
                    Job # {oldJob.id}
                  </SS_Text>
                  <SS_Text className="m-0 px-8 py-3 text-black truncate">
                    {oldJob.plot?.formatted_address}
                  </SS_Text>
                </div>
                
                <ScrollArea className="flex-1 border-b border-gray-300 max-h-32">
                  <div className="p-6 flex flex-row gap-4">
                    <SS_TabPill
                      label="Information"
                      active={activeTab === 'info'}
                      onPress={() => _setTab(0, 'info')}
                    />

                    <SS_TabPill
                      label="Deliveries"
                      active={activeTab === 'deliveries'}
                      onPress={() => _setTab(1, 'deliveries')}
                    />

                    <SS_TabPill
                      label="Specification"
                      active={activeTab === 'specification'}
                      onPress={() => _setTab(2, 'specification')}
                    />

                    <SS_TabPill
                      label="Tasks"
                      active={activeTab === 'tasks'}
                      onPress={() => _setTab(3, 'tasks')}
                    />

                    <SS_TabPill
                      label="Notes"
                      active={activeTab === 'notes'}
                      onPress={() => _setTab(4, 'notes')}
                    />

                    <SS_TabPill
                      label="Photos"
                      active={activeTab === 'photos'}
                      onPress={() => _setTab(5, 'photos')}
                    />
                  </div>
                </ScrollArea>

                <ScrollArea className="flex-1">
                  <div className="px-8 py-6">
                    {activeTab === 'info' && (
                      <div>
                        <JobCustomerView job={oldJob} />
                        <SS_Divider />
                        <JobDetailsView
                          job={oldJob}
                          fullDescription={true}
                          includeOps={true}
                        />
                      </div>
                    )}

                    {activeTab === 'deliveries' && (
                      <div>
                        <SS_Label>Delivery Information</SS_Label>
                        <div className="border-b border-gray-300 mb-3" />
                        {oldJob.deliveries && oldJob.deliveries.trim() !== '' ? (
                          <SS_Text className="text-gray-800">{oldJob.deliveries}</SS_Text>
                        ) : (
                          <SS_Text className="text-gray-800">
                            There are no deliveries booked for this job
                          </SS_Text>
                        )}
                      </div>
                    )}

                    {activeTab === 'specification' && (
                      <div>
                        {specArr.length > 0 ? (
                          specArr.map((spec, key) => (
                            <div key={key}>
                              <SS_Label>{spec[0]}</SS_Label>
                              <div className="border-b border-gray-300 mb-3" />
                              <SS_Text className="text-gray-800">{spec[1]}</SS_Text>
                            </div>
                          ))
                        ) : (
                          <div>
                            <SS_Label>Specifications</SS_Label>
                            <div className="border-b border-gray-300 mb-3" />
                            <SS_Text className="text-gray-800">
                              There are no specifications for this job
                            </SS_Text>
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === 'tasks' && (
                      <div>
                        <SS_Label>Tasks</SS_Label>
                        <div className="border-b border-gray-300 mb-3" />

                        {!oldJob.tasks || !oldJob.tasks.length ? (
                          <SS_Text className="text-gray-800">
                            There are no tasks for this job
                          </SS_Text>
                        ) : (
                          oldJob.tasks.map((task: any, key: number) => {
                            let bgColor = 'bg-gray-200';
                            let textColor = 'text-gray-800';
                            let status = 'TO DO';

                            if (task.started_at && !task.completed_at) {
                              status = 'IN PROGRESS';
                              bgColor = 'bg-orange-200';
                              textColor = 'text-orange-800';
                            }

                            if (task.started_at && task.completed_at) {
                              status = 'COMPLETED';
                              bgColor = 'bg-green-200';
                              textColor = 'text-green-800';
                            }

                            return (
                              <div
                                key={'t-' + key}
                                className="flex-1 flex flex-row items-center border-b border-gray-300"
                              >
                                <div className="flex-1 flex flex-row py-3">
                                  <SS_Text className="text-black m-0">
                                    {task.description || ''}
                                  </SS_Text>
                                </div>

                                <div className={`ml-2 p-1 rounded ${bgColor}`}>
                                  <SS_Text className={`text-xs uppercase ${textColor} my-0`}>
                                    {status}
                                  </SS_Text>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}

                    {activeTab === 'notes' && (
                      <div>
                        <SS_Label>Notes</SS_Label>
                        <div className="border-b border-gray-300 mb-3" />

                        {!oldJob.notes || !oldJob.notes.length ? (
                          <SS_Text className="text-gray-800">
                            There are no notes for this job
                          </SS_Text>
                        ) : (
                          oldJob.notes.map((note: any, key: number) => (
                            <div
                              key={'n-' + key}
                              className="flex-1 border-b border-gray-300 py-3"
                            >
                              <div className="flex-1 flex flex-row">
                                <SS_Text className="text-xs uppercase font-bold m-0 mb-2">
                                  {note.user?.name}
                                </SS_Text>
                                <SS_Text className="flex-1 text-xs uppercase font-bold m-0 mb-2 text-right">
                                  {moment(note.created_at).format('dddd, DD MMMM YYYY')}
                                </SS_Text>
                              </div>
                              <SS_Text className="text-black m-0">
                                {note.note}
                              </SS_Text>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {activeTab === 'photos' && (
                      <div>
                        <SS_Label>Photos</SS_Label>
                        <div className="flex flex-wrap flex-row border-t border-gray-300 mb-3">
                          {!oldJob.photos || !oldJob.photos.length ? (
                            <SS_Text className="text-gray-800">
                              There are no photos for this job
                            </SS_Text>
                          ) : (
                            oldJob.photos.map((photo: any, key: number) => {
                              const imageUrl = `${import.meta.env.VITE_API_URL || ''}image/${oldJob.id}/${photo.filename}`;

                              return (
                                <div
                                  key={'p-' + key}
                                  className="flex-shrink-0 w-48 m-2 border border-gray-300"
                                >
                                  <img
                                    className="w-48 h-48 object-contain"
                                    src={imageUrl}
                                    alt={photo.tag}
                                  />
                                  <div className="border-t border-gray-300 p-2">
                                    <SS_Text className="text-black m-0">
                                      {photo.tag}
                                    </SS_Text>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}